import React, { ChangeEvent, FC, useState } from 'react';
import { AccordionSummary, Box, Checkbox, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CollectiveInvoiceSummary } from 'models/invoice';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { formatCurrency } from 'utils/format';

type InvoiceSummaryProps = {
    invoice: CollectiveInvoiceSummary;
    currency: string;
    totalPrice: number;
    noOfOrders: number;
    isEnabled: boolean;
    onPaymentTermChange: (paymentTerm: number, invoiceKey: string) => void;
    onInvoiceNumberChange: (invoiceNumber: string, invoiceKey: string) => void;
    onInvoiceToggle: (isEnabled: boolean) => void;
};
const InvoiceSummary: FC<InvoiceSummaryProps> = ({
    invoice,
    currency,
    totalPrice,
    noOfOrders,
    isEnabled,
    onPaymentTermChange,
    onInvoiceNumberChange,
    onInvoiceToggle,
}: InvoiceSummaryProps) => {
    const { t: translate } = useTranslation(['orders', 'invoices']);
    const [paymentTerm, setPaymentTerm] = useState(String(invoice.paymentTerm));
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const currentOrg = currentOrgVar();

    const handlePaymentTermChange = (paymentTerm: string) => {
        setPaymentTerm(paymentTerm);
        onPaymentTermChange(parseInt(paymentTerm, 10), invoice.key);
    };

    const handleInvoiceNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        const invNumber = e.target.value;
        setInvoiceNumber(invNumber);
        onInvoiceNumberChange(invNumber, invoice.key);
    };

    const isManualInvoiceNumber = () => {
        return currentOrg?.invoicing?.configuration?.invoiceNumberType === 'MANUAL';
    };

    return (
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            disableRipple
        >
            <Box display="flex" width="100%" alignItems="center">
                <Checkbox
                    checked={isEnabled}
                    onChange={(e) => onInvoiceToggle(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    color="secondary"
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 32 } }}
                    inputProps={{ 'aria-label': `Enable invoice generation for ${invoice.key}` }}
                />
                <Box flex="1 0 40%">
                    <Box>
                        <Typography>{invoice.displayLabel}</Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ color: 'text.secondary' }} variant="caption">
                            {translate(`invoices:${invoice.deliveryMethod}`)}&nbsp;
                            {invoice.deliveryMethod === 'EMAIL' ? invoice.emails.join(', ') : undefined}
                        </Typography>
                    </Box>
                </Box>
                <Box mr={2}>
                    <Typography sx={{ color: totalPrice < 0 ? 'red' : 'text.secondary', whiteSpace: 'nowrap' }}>
                        {noOfOrders} {noOfOrders === 1 ? translate('orders:ORDER') : translate('orders:ORDERS')},{' '}
                        {formatCurrency(totalPrice, currency)}
                    </Typography>
                </Box>
                <Box mr={2} sx={{ maxWidth: '150px' }}>
                    <NumberOnlyTextField
                        onFocus={(e) => {
                            e.stopPropagation();
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        onValueChange={handlePaymentTermChange}
                        label={translate('invoices:PAYMENT_TERM')}
                        hasDecimal={false}
                        maxLength={4}
                        value={paymentTerm}
                        inputProps={{ style: { textAlign: 'right' } }}
                    />
                </Box>
                {isManualInvoiceNumber() ? (
                    <Box mr={2} sx={{ maxWidth: '150px' }}>
                        <TextField
                            onFocus={(e) => {
                                e.stopPropagation();
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            onChange={handleInvoiceNumberChange}
                            required={true}
                            label={translate('invoices:INVOICE_NUMBER')}
                            value={invoiceNumber}
                            inputProps={{ style: { textAlign: 'right', pointerEvents: 'none' } }}
                        />
                    </Box>
                ) : undefined}
            </Box>
        </AccordionSummary>
    );
};

export default InvoiceSummary;
